import { useEffect, useRef, type ReactNode } from "react";
import { HlsPlayer, NativePlayer, nativePlayerBridge, type NativeVideoBounds, type Player, type PlayerState } from "@hybrid-iptv/player";
import { stopUrlForSource, usePlayerStore } from "./playerStore.js";
import { PlayerSurface } from "./PlayerSurface.js";
import { MultiviewOverlay } from "./MultiviewOverlay.js";

/**
 * The native engine draws the picture BEHIND the page. Publish where the video
 * box is so app.css ("Native video") can cut a see-through window into the
 * page there, or close it when no video shows.
 */
function showNativeVideo(bounds: NativeVideoBounds | null): void {
  const root = document.documentElement;
  root.classList.toggle("native-video--showing", bounds !== null);
  if (!bounds) return;
  root.style.setProperty("--native-video-x", `${bounds.x}px`);
  root.style.setProperty("--native-video-y", `${bounds.y}px`);
  root.style.setProperty("--native-video-w", `${bounds.width}px`);
  root.style.setProperty("--native-video-h", `${bounds.height}px`);
}

/**
 * Owns the single Player instance for the whole app (technical-spec §6.1).
 * The instance is constructed once and never lives in React state; its
 * `statechange` events are mirrored into the Zustand store so navigation
 * between routes never remounts or interrupts playback. The <video> element
 * is rendered by <PlayerSurface> but remains mounted above the router.
 */
export function PlayerProvider({ children }: { children: ReactNode }) {
  const attach = usePlayerStore((state) => state.attach);
  const detach = usePlayerStore((state) => state.detach);
  const sync = usePlayerStore((state) => state.sync);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || playerRef.current) return;
    // Inside the APK the native engine (ExoPlayer) plays straight from the
    // provider; everywhere else the web engine does. Detected, never assumed.
    const bridge = nativePlayerBridge();
    const player = bridge ? new NativePlayer({ video, bridge, onBounds: showNativeVideo }) : new HlsPlayer({ video });
    if (bridge) document.documentElement.classList.add("native-video");
    playerRef.current = player;
    attach(player);
    const unsubscribe = player.on("statechange", (payload) => {
      sync(payload as PlayerState);
    });
    return () => {
      unsubscribe();
      player.destroy();
      playerRef.current = null;
      document.documentElement.classList.remove("native-video", "native-video--showing");
      detach();
    };
  }, [attach, detach, sync]);

  // Closing the TAB (as opposed to the player's ✕ button) never runs
  // playerStore.close(), so the transcoded session would otherwise sit on the
  // server until the idle sweep. pagehide is the one reliable moment to say
  // "done" — keepalive lets the fetch outlive the page.
  useEffect(() => {
    const onPageHide = () => {
      const stopUrl = stopUrlForSource(usePlayerStore.getState().source);
      if (stopUrl) void fetch(stopUrl, { keepalive: true }).catch(() => undefined);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  return (
    <>
      {children}
      <PlayerSurface videoRef={videoRef} />
      {/* Section 14 step 3: the multiview grid stacks above everything while
          active; each tile is its own Player instance (see MultiviewOverlay). */}
      <MultiviewOverlay />
    </>
  );
}
